import type { BooPermissions, BooRole, FeatureLevel, PersonRow, PersonUpdate, RoleFeature } from "./api.ts";

export const ADMIN_ROLE = "admin";
export const STANDARD_ROLE = "member";

export type LevelKey = "owner" | "admin" | "standard" | "blocked";

export interface LevelOption {
  key: LevelKey;
  label: string;
  hint: string;
}

function isCustomGroup(role: string): boolean {
  return role !== ADMIN_ROLE && role !== STANDARD_ROLE;
}

export function customGroups(roles: Record<string, unknown>): string[] {
  return Object.keys(roles)
    .filter(isCustomGroup)
    .sort((a, b) => a.localeCompare(b));
}

export function groupLabel(role: string): string {
  if (role === ADMIN_ROLE) return "Admins";
  if (role === STANDARD_ROLE) return "Standard";
  return role;
}

export function levelOf(p: PersonRow): LevelKey {
  if (p.blocked) return "blocked";
  if (p.kind === "managed" && p.booRole === "owner") return "owner";
  if (p.kind === "managed" && p.booRole === "admin") return "admin";
  return "standard";
}

export function levelLabel(key: LevelKey): string {
  if (key === "owner") return "Owner";
  if (key === "admin") return "Admin";
  if (key === "blocked") return "Blocked";
  return "Standard";
}

function managesTarget(p: PersonRow, perms: Partial<BooPermissions> | undefined): boolean {
  if (p.kind === "managed" && (p.booRole === "owner" || p.booRole === "admin")) {
    return perms?.canManageAdmins === true;
  }
  return perms?.canManageMembers === true;
}

export function canChangeLevel(
  p: PersonRow,
  perms: Partial<BooPermissions> | undefined,
  myUserId: string | undefined,
): boolean {
  if (p.personId === myUserId) return false;
  return managesTarget(p, perms);
}

export function chatGroups(p: Pick<PersonRow, "groups" | "keptGroups">): string[] {
  return [...new Set([...p.groups, ...(p.keptGroups ?? [])])];
}

export type ChatSummary = (base: string, groups: string[]) => string;

export type DashboardLevel = Exclude<LevelKey, "blocked">;

// The dashboard levels in the People tab's words. Owner and Admin are for a
// Boo owner to give (canManageAdmins); an admin gives Standard only.
export function dashboardLevels(perms: Partial<BooPermissions> | undefined): Array<LevelOption & { key: DashboardLevel }> {
  return [
    ...(perms?.canManageAdmins
      ? [
          { key: "owner" as const, label: "Owner", hint: "Manages this Boo, including its admins." },
          { key: "admin" as const, label: "Admin", hint: "Changes this Boo's settings and people." },
        ]
      : []),
    { key: "standard" as const, label: "Standard", hint: "Can open this Boo but not change it." },
  ];
}

export function booRoleOfLevel(level: DashboardLevel): BooRole {
  return level === "standard" ? "member" : level;
}

export function levelOfRole(role: BooRole): DashboardLevel {
  return role === "member" ? "standard" : role;
}

export function levelOptions(p: PersonRow, perms: Partial<BooPermissions> | undefined): LevelOption[] {
  const options: LevelOption[] =
    p.kind === "managed"
      ? dashboardLevels(perms)
      : [{ key: "standard", label: "Standard", hint: "Uses Boo in chat; no dashboard." }];
  options.push({ key: "blocked", label: "Blocked", hint: "Boo won't reply to them." });
  return options;
}

const RANK: Record<FeatureLevel, number> = { disabled: 0, confirm: 1, auto: 2 };

export function summedLevel(
  roles: Record<string, { features: Record<string, FeatureLevel> }>,
  base: string,
  groups: string[],
  feature: string,
): FeatureLevel {
  const bases = base === ADMIN_ROLE ? [ADMIN_ROLE, STANDARD_ROLE] : [base];
  return [...bases, ...groups]
    .map((r) => featureLevel(roles, r, feature))
    .reduce<FeatureLevel>((best, l) => (RANK[l] > RANK[best] ? l : best), "disabled");
}

export function isBelow(level: FeatureLevel, floor: FeatureLevel): boolean {
  return RANK[level] < RANK[floor];
}

// What Standard already gives Admins for a feature, when that's more than the
// Admins group's own setting; null when Standard adds nothing.
export function adminFloor(
  roles: Record<string, { features: Record<string, FeatureLevel> }>,
  feature: string,
): FeatureLevel | null {
  const standard = featureLevel(roles, STANDARD_ROLE, feature);
  return isBelow(featureLevel(roles, ADMIN_ROLE, feature), standard) ? standard : null;
}

export function chatSummary(
  roles: Record<string, { features: Record<string, FeatureLevel> }>,
  features: RoleFeature[],
  base: string,
  groups: string[] = [],
): string {
  const items = gridFeatures(features).flatMap((f) => {
    const level = summedLevel(roles, base, groups, f.id);
    if (level === "disabled") return [];
    const title = featureCopy(f).title;
    return [level === "confirm" ? `${title} (confirm first)` : title];
  });
  return items.length ? `In chat: ${items.join(", ")}.` : "In chat: none of the extra features.";
}

export const LINKABLE_PROVIDERS: ReadonlySet<string> = new Set(["slack", "teams", "gchat", "discord"]);

export function platformLabel(provider: string): string {
  return PLATFORM_NAME[provider] ?? "chat";
}

export function levelPatch(p: PersonRow, to: LevelKey): PersonUpdate {
  if (to === levelOf(p)) return {};
  if (to === "blocked") return { blocked: true };
  const patch: PersonUpdate = p.blocked ? { blocked: false } : {};
  if (p.kind !== "managed") return patch;
  const role = to === "owner" || to === "admin" ? to : "member";
  if (role !== (p.booRole ?? "member")) patch.role = role;
  return patch;
}

function nameTokens(name: string | null | undefined): string[] {
  return (name ?? "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

function namesAgree(chat: string[], managed: string[]): boolean {
  if (chat.length < 2 || managed.length < 2) return false;
  if (chat[0] !== managed[0]) return false;
  const a = chat[chat.length - 1];
  const b = managed[managed.length - 1];
  return a.startsWith(b) || b.startsWith(a);
}

export function likelyMatch(p: PersonRow, people: PersonRow[]): PersonRow | null {
  if (p.kind !== "chat_only" || p.handles.length === 0) return null;
  if (!p.handles.every((h) => LINKABLE_PROVIDERS.has(h.provider))) return null;
  const providers = new Set(p.handles.map((h) => h.provider));
  const email = p.email?.trim().toLowerCase();
  const tokens = nameTokens(p.name);
  const candidates = people.filter(
    (m) =>
      m.kind === "managed" &&
      !m.handles.some((h) => providers.has(h.provider)) &&
      ((!!email && m.email?.trim().toLowerCase() === email) || namesAgree(tokens, nameTokens(m.name))),
  );
  return candidates.length === 1 ? candidates[0] : null;
}

const PLATFORM_NAME: Record<string, string> = {
  slack: "Slack",
  teams: "Teams",
  gchat: "Google Chat",
  discord: "Discord",
};

const RAW_ID =
  /^(installer-\d+|[UWB](?=[A-Z0-9]*\d)[A-Z0-9]{6,}|\d{15,}|users\/\d+|(aad|bf):\S+|29:\S+|(slack|teams|gchat|discord)[:-]\S+)$/;

type Nameable = Pick<PersonRow, "name"> & Partial<Pick<PersonRow, "email" | "personId" | "handles">>;

export function rawIdName(p: Nameable): string | null {
  const name = p.name?.trim();
  if (!name) return null;
  if (name === p.personId || p.handles?.some((h) => h.providerUserId === name) || RAW_ID.test(name)) return name;
  return null;
}

export function displayName(p: Nameable): string {
  if (rawIdName(p)) {
    const platform = PLATFORM_NAME[p.handles?.[0]?.provider ?? ""];
    return platform ? `Unknown ${platform} user` : "Unknown person";
  }
  return p.name || p.email || "Someone";
}

const DASHBOARD_POWER: Record<"owner" | "admin" | "viewer", string> = {
  owner: "can do everything with this Boo, including choosing owners and admins",
  admin: "can change this Boo's settings, apps, secrets and channels, and add people",
  viewer: "can open this Boo in the dashboard but can't change it",
};

export function consequence(p: PersonRow, to: LevelKey, isMe: boolean, inChat?: ChatSummary): string {
  const who = isMe ? "You" : displayName(p);
  const them = isMe ? "you" : displayName(p);
  if (to === "blocked") {
    return `Boo stops answering ${them} in chat. ${who} can still open this Boo in the dashboard.`;
  }
  const back = p.blocked ? `Boo answers ${them} in chat again. ` : "";
  const base = to === "owner" || to === "admin" ? ADMIN_ROLE : STANDARD_ROLE;
  const chat = inChat?.(base, chatGroups(p)) ?? "";
  const power = to === "owner" ? DASHBOARD_POWER.owner : to === "admin" ? DASHBOARD_POWER.admin : DASHBOARD_POWER.viewer;
  return `${back}${who} ${power}. ${chat}`.trim();
}

export interface FeatureCopy {
  title: string;
  summary: string;
}

const FEATURE_COPY: Record<string, FeatureCopy> = {
  deep_thinking: {
    title: "Deep thinking",
    summary: "Boo switches to a slower, stronger model for the rest of a conversation. Better on hard problems; every reply costs more.",
  },
  coding_agent_bridge: {
    title: "Connect Claude Code or Cursor",
    summary: "Developers can ask Boo questions from Claude Code or Cursor. Each exchange is saved as a conversation with Boo.",
  },
  runtime_feedback: {
    title: "Send problem reports to Boo Lab",
    summary: "When one of Boo's own tools or tasks fails, Boo sends Boo Lab a diagnostic report that includes the conversation where it happened.",
  },
};

export function featureCopy(f: Pick<RoleFeature, "id" | "title" | "summary">): FeatureCopy {
  return FEATURE_COPY[f.id] ?? { title: f.title, summary: f.summary };
}

export const PROBLEM_REPORTS = "runtime_feedback";

export function gridFeatures(features: RoleFeature[]): RoleFeature[] {
  return features.filter((f) => f.id !== PROBLEM_REPORTS);
}

export const LEVEL_LABEL: Record<FeatureLevel, string> = {
  auto: "On",
  confirm: "Confirm first",
  disabled: "Off",
};

export const LEVEL_HINT: Record<FeatureLevel, string> = {
  auto: "Boo uses it whenever someone in this group asks.",
  confirm: "Boo asks the person who requested it to approve first. Nobody else is asked.",
  disabled: "Boo says no and names who can help. If someone whose group has it On asks in the same conversation, Boo goes ahead.",
};

export function hasRole(roles: Record<string, unknown>, role: string): boolean {
  return Object.hasOwn(roles, role);
}

export function featureLevel(roles: Record<string, { features: Record<string, FeatureLevel> }>, role: string, feature: string): FeatureLevel {
  if (!hasRole(roles, role)) return "disabled";
  return roles[role]?.features?.[feature] ?? "disabled";
}

export function withLevel<R extends { features: Record<string, FeatureLevel> }>(
  roles: Record<string, R>,
  role: string,
  feature: string,
  level: FeatureLevel,
): Record<string, R> {
  const current = hasRole(roles, role) ? roles[role] : undefined;
  if (!current) return roles;
  return { ...roles, [role]: { ...current, features: { ...current.features, [feature]: level } } };
}

export function groupNameProblem(name: string, existing: string[]): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Give the group a name.";
  if (trimmed.length > 60) return "Keep the name under 60 characters.";
  const lower = trimmed.toLowerCase();
  const taken = [...existing, "admin", "member", "admins", "standard", "owner", "owners", "blocked"];
  if (taken.some((t) => t.toLowerCase() === lower)) return "That name is already used.";
  if (trimmed.includes("+")) return "Group names can't contain +.";
  const reserved = ["system", ...Object.getOwnPropertyNames(Object.prototype)];
  if (reserved.some((r) => r.toLowerCase() === lower)) return "That name is reserved. Pick another.";
  return null;
}
